import React, { createContext, useContext, useState, useEffect } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { loadSupabase, whenSupabaseReady } from '@/integrations/supabase/ready';

// supabase-js keeps the session under sb-<project ref>-auth-token.
const SESSION_KEY = 'sb-ebqadjyvvdjdscfkgzou-auth-token';

/** Whether this visit may carry a session: one saved by an earlier sign-in, or
 *  one arriving in the URL from an auth email (sign-up, password reset). Only
 *  then is the Supabase library loaded up front; otherwise the visitor is
 *  signed out and it waits until a page that needs it loads it. */
function sessionMayExist() {
  try {
    if (localStorage.getItem(SESSION_KEY)) return true;
  } catch {
    // storage blocked — fall through to the URL check
  }
  return /access_token=|refresh_token=|error_description=/.test(
    window.location.hash + window.location.search,
  );
}

interface Profile {
  id: string;
  full_name: string;
  email?: string;
  email_encrypted?: string | null;
  phone?: string;
  phone_encrypted?: string | null;
  preferred_lang: 'he' | 'en';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
  isAdmin: boolean;
  verifyAdminStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [mayHaveSession] = useState(sessionMayExist);
  const [loading, setLoading] = useState(mayHaveSession);

  useEffect(() => {
    let cancelled = false;
    let subscription: { unsubscribe: () => void } | undefined;

    // Subscribe as soon as the client exists — created below when a session
    // may exist, or later by whichever page needs Supabase.
    const cancelWait = whenSupabaseReady((supabase) => {
      if (cancelled) return;
      // Set up auth state listener
      ({ data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          setSession(session);
          setUser(session?.user ?? null);
        
          if (session?.user) {
            // Fetch user profile and role
            setTimeout(async () => {
              try {
                const { data: profileData } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('id', session.user.id)
                  .single();
              
                setProfile(profileData);

                // Fetch role from user_roles table
                const { data: roleData } = await supabase
                  .from('user_roles')
                  .select('role')
                  .eq('user_id', session.user.id)
                  .maybeSingle();
              
                setRole(roleData?.role || null);
              } catch (error) {
                console.error('Error fetching profile:', error);
              }
            }, 0);
          } else {
            setProfile(null);
            setRole(null);
          }
        }
      ));

      // Check for existing session
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled) return;
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      });
    });

    if (mayHaveSession) loadSupabase();

    return () => {
      cancelled = true;
      cancelWait();
      subscription?.unsubscribe();
    };
  }, [mayHaveSession]);

  const signIn = async (email: string, password: string) => {
    const supabase = await loadSupabase();
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    const supabase = await loadSupabase();
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
      }
    });
    return { error };
  };

  const signOut = async () => {
    const supabase = await loadSupabase();
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const resetPassword = async (email: string) => {
    const supabase = await loadSupabase();
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const supabase = await loadSupabase();
    const { error } = await supabase.auth.updateUser({
      password: password,
    });
    return { error };
  };

  // Server-side admin verification - CRITICAL SECURITY
  const verifyAdminStatus = async (): Promise<boolean> => {
    if (!session) {
      return false;
    }

    try {
      const supabase = await loadSupabase();
      const { data, error } = await supabase.functions.invoke('verify-admin', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error verifying admin status:', error);
        return false;
      }

      return data?.isAdmin || false;
    } catch (error) {
      console.error('Unexpected error verifying admin status:', error);
      return false;
    }
  };

  const isAdmin = role === 'admin';

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        session, 
        profile, 
        loading, 
        signIn, 
        signUp, 
        signOut,
        resetPassword,
        updatePassword,
        isAdmin,
        verifyAdminStatus
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};